/* ============================================================
 * Free Shipping Threshold Bar (ecomm)
 * Custom element: <shipping-bar>
 *
 * Markup is rendered by snippets/ecomm-free-shipping-bar.liquid.
 *
 * Hooks into existing cart events on the jawscleans (Motion) theme:
 *   - cart:updated         dispatched after quantity changes (theme.js)
 *   - ajaxProduct:added    dispatched after product added (theme.js)
 *   - cart:build           dev-friendly rebuild
 *   - cartChange           custom event used by some integrations
 *
 * Each handler resolves the new cart total and updates the bar
 * (progress, messaging, remaining-amount).
 * ============================================================ */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !('customElements' in window)) {
    return;
  }

  if (customElements.get('shipping-bar')) {
    return;
  }

  function getLang() {
    if (typeof navigator === 'undefined') return 'en-US';
    if (navigator.languages && navigator.languages.length) return navigator.languages[0];
    return navigator.language || 'en-US';
  }

  function formatMoneyIntl(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return amount.toLocaleString(getLang(), {
        style: 'currency',
        currency: currency || 'USD'
      });
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  // Try to use the theme's money formatter when available so the
  // bar matches the rest of the storefront (e.g. "USD $30.00").
  function formatMoneyTheme(cents, fallbackCurrency) {
    try {
      if (
        window.theme &&
        window.theme.Currency &&
        typeof window.theme.Currency.formatMoney === 'function' &&
        window.theme.settings &&
        window.theme.settings.moneyFormat
      ) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return formatMoneyIntl(cents, fallbackCurrency);
  }

  var cartFetchInflight = null;

  function fetchCartTotal() {
    if (cartFetchInflight) return cartFetchInflight;

    var url = '/cart.js?t=' + Date.now();
    if (window.theme && window.theme.routes && window.theme.routes.cart) {
      url = window.theme.routes.cart + (window.theme.routes.cart.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now();
    }

    cartFetchInflight = fetch(url, {
      credentials: 'same-origin',
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartFetchInflight = null;
        return cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
      })
      .catch(function () {
        cartFetchInflight = null;
        return null;
      });

    return cartFetchInflight;
  }

  class ShippingBar extends HTMLElement {
    constructor() {
      super();

      this.currency = this.getAttribute('data-currency') || 'USD';

      this.pendingEl = this.querySelector('[data-free-shipping-pending]');
      this.pendingAmountEl = this.pendingEl ? this.pendingEl.querySelector('[data-amount]') : null;
      this.metEl = this.querySelector('[data-free-shipping-met]');
      this.progress = this.querySelector('progress');

      var threshold = this.getAttribute('data-threshold');
      this.threshold = threshold ? parseInt(threshold, 10) : 0;

      var total = this.getAttribute('data-total');
      this.cartTotal = total ? parseInt(total, 10) : 0;

      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onProductAdded = this._onProductAdded.bind(this);
      this._onCartBuild = this._onCartBuild.bind(this);
      this._onCartChange = this._onCartChange.bind(this);
    }

    connectedCallback() {
      // Server-rendered values are already correct, but render once
      // to make sure JS-only formatting (e.g. live remaining amount)
      // is applied.
      this.updateShipping();

      document.addEventListener('cart:updated', this._onCartUpdated);
      document.addEventListener('ajaxProduct:added', this._onProductAdded);
      document.addEventListener('cart:build', this._onCartBuild);
      document.addEventListener('cartChange', this._onCartChange);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('ajaxProduct:added', this._onProductAdded);
      document.removeEventListener('cart:build', this._onCartBuild);
      document.removeEventListener('cartChange', this._onCartChange);
    }

    _onCartUpdated(e) {
      if (e && e.detail && e.detail.cart && typeof e.detail.cart.total_price === 'number') {
        this.cartTotal = e.detail.cart.total_price;
        this.updateShipping();
        return;
      }
      this._refreshFromCartEndpoint();
    }

    _onProductAdded() {
      this._refreshFromCartEndpoint();
    }

    _onCartBuild() {
      this._refreshFromCartEndpoint();
    }

    _onCartChange(e) {
      if (e && e.detail && typeof e.detail.cartTotal === 'number') {
        this.cartTotal = e.detail.cartTotal;
        this.updateShipping();
        return;
      }
      this._refreshFromCartEndpoint();
    }

    _refreshFromCartEndpoint() {
      var self = this;
      fetchCartTotal().then(function (total) {
        if (total === null) return;
        self.cartTotal = total;
        self.updateShipping();
      });
    }

    updateShipping() {
      if (!this.threshold) return;

      var total = Number(this.cartTotal) || 0;
      var met = total >= this.threshold;

      if (this.pendingEl) {
        this.pendingEl.classList.toggle('is-hidden', met);
      }
      if (this.metEl) {
        this.metEl.classList.toggle('is-hidden', !met);
      }

      if (this.progress) {
        var value = Math.min(total, this.threshold);
        this.progress.setAttribute('value', String(value));
        this.progress.setAttribute('max', String(this.threshold));
      }

      if (this.pendingAmountEl) {
        var remaining = this.threshold - total;
        if (remaining < 0) remaining = 0;
        this.pendingAmountEl.textContent = formatMoneyTheme(remaining, this.currency);
      }
    }
  }

  customElements.define('shipping-bar', ShippingBar);
})();
